import {
  APPROVAL_SIMULATION_AHEAD_NOTE,
  APPROVAL_SIMULATION_NOTE,
  type ApprovalListItem,
  type ApprovalPreviewKind,
} from '@fos/shared/contracts/approvals';
import { toLocalDate } from '@fos/shared/time';
import {
  Brain,
  type LucideIcon,
  Mail,
  ReceiptText,
  Rocket,
  ShieldCheck,
  SlidersHorizontal,
  SquareCheckBig,
} from 'lucide-react';
import { formatDateShort, formatTime } from '../../lib/format';

/** Ikona typu na kafelku accent/subtle (Figma 03: mail, deploy, zadania). */
export const PREVIEW_KIND_ICONS: Record<ApprovalPreviewKind, LucideIcon> = {
  email: Mail,
  invoice: ReceiptText,
  task_proposals: SquareCheckBig,
  memory: Brain,
  offer_terms: SlidersHorizontal,
  deploy: Rocket,
  generic: ShieldCheck,
};

/**
 * Baner symulacji wg rodzaju akcji (kompozycja Fali 5): wdrożenie nic nie „wysyła” — „nic nie zostanie wdrożone”
 * (przed decyzją) i „nic nie zostało wdrożone” (po wykonaniu atrapą); pozostałe akcje — teksty ogólne.
 */
export function simulationNote(kind: ApprovalPreviewKind, when: 'ahead' | 'done'): string {
  if (kind === 'deploy')
    return when === 'ahead' ? 'Symulacja — nic nie zostanie wdrożone' : 'Symulacja — nic nie zostało wdrożone';
  return when === 'ahead' ? APPROVAL_SIMULATION_AHEAD_NOTE : APPROVAL_SIMULATION_NOTE;
}

/** Czas na karcie: „8:12” dziś, „wczoraj”, inaczej „8.10”. */
export function approvalTime(value: Date | string, now: Date = new Date()): string {
  const date = value instanceof Date ? value : new Date(value);
  const day = toLocalDate(date);
  if (day === toLocalDate(now)) return formatTime(date);
  if (day === toLocalDate(new Date(now.getTime() - 86_400_000))) return 'wczoraj';
  return formatDateShort(date, now);
}

/** Kto przygotował (meta karty): „przygotował Copilot”, „Worker”, „Seban”. */
export function initiatorText(item: Pick<ApprovalListItem, 'initiator'>): string {
  switch (item.initiator.type) {
    case 'ai':
      return 'przygotował Copilot';
    case 'mcp':
      return 'przygotował agent (MCP)';
    case 'worker':
      return 'Worker';
    default:
      return item.initiator.label;
  }
}

/** Meta karty (Figma 03): „Email · REA · przygotował Copilot · 08:12” (+ „testy 12/12”). */
export function approvalMeta(item: ApprovalListItem, opts: { now?: Date; compact?: boolean } = {}): string {
  const parts = [item.typeLabel];
  if (item.client) parts.push(item.client.name);
  if (!opts.compact) parts.push(initiatorText(item));
  if (item.hint) parts.push(item.hint);
  parts.push(approvalTime(item.createdAt, opts.now));
  return parts.join(' · ');
}

/** Typy, w których zatwierdzenie dzieje się w kontekście rekordu („Przejrzyj”), np. propozycje ze spotkania. */
export function isReviewKind(kind: ApprovalPreviewKind): boolean {
  return kind === 'task_proposals';
}
